import React, { useState } from "react";
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import axios from 'axios';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import { Button, Typography, Snackbar } from "@mui/material";

const columns = [
  { id: 'no', label: 'No', width: '5%' },
  { id: 'uid', label: 'UID', width: '10%' },
  { id: 'server', label: 'Server', width: '10%' },
  { id: 'detail', label: 'Detail', width: 'auto' },
];

function createData(no, uid, server, detail) {
  return { no, uid, server, detail };
}

const GenshinImpact = ( {selectedText} ) => {
  const [searchId, setSearchId] = React.useState('');
  const [data, setData] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [message, setMessage] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);

  const handleSearch = React.useCallback(async () => {
    if (!searchId) {
      setError('ID pencarian tidak boleh kosong');
      setData([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/game/get?id=${searchId}&game=Genshin Impact`, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_API_KEY}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data.status === 'success') {
        setData(response.data.data.map((item, index) => createData(item.id, item.uid, item.server, item.detail)));
        setError('');
      } else {
        setData([]);
        setError(`Data tidak ditemukan untuk UID: ${searchId}`);
      }
    } catch (err) {
      setData([]);
      if (err.response && err.response.data) {
        setError(err.response.data.message);
      } else {
        setError("Terjadi kesalahan saat mencari data");
      }
    } finally {
      setLoading(false);
    }
  }, [searchId]);

  const handleReset = () => {
    setFormData({ id: "", uid: "", server: "", detail: "" });
    setIsEditMode(false);
  };

  const [formData, setFormData] = useState({
    uid: "",
    server: "",
    detail: "",
    game: "Genshin Impact",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/game/delete`,
        { id, deleted: 1 },
        {
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_API_KEY}`,
            "Content-Type": "application/json",
          },
        }
      );
  
      if (response.data.status === "success") {
        setMessage(response.data.message);
      } else {
        setMessage(response.data.message);
      }
    } catch (err) {
      setMessage("Terjadi kesalahan saat mengirim data.");
    } finally {
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.uid || !formData.server || formData.detail === "") {
      setMessage("Semua field harus diisi.");
      return;
    }

    try {
      const url = isEditMode
        ? `${process.env.REACT_APP_API_URL}/game/edit`
        : `${process.env.REACT_APP_API_URL}/game/insert`;

      const method = isEditMode ? "put" : "post";

      const response = await axios[method](url, formData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_API_KEY}`,
        },
      });

      if (response.data.status === "success") {
        setMessage(response.data.message);
        handleReset();
      } else {
        setMessage(response.data.message);
      }
    } catch (err) {
      setMessage("Terjadi kesalahan saat mengirim data.");
    } finally {
    }
  };

  const handleEdit = (item) => {
    setFormData({
      id: item.no,
      uid: item.uid,
      server: item.server,
      detail: item.detail,
    });
    setIsEditMode(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  React.useEffect(() => {
    if (searchId.length >= 4) {
      const timeoutId = setTimeout(handleSearch, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [searchId, handleSearch]);

  const highlightText = (text, highlight) => {
    if (typeof text !== 'string') {
      return null;
    }
  
    const lines = text.split(/(\r\n|\n|\r)/g).map((line, index) => 
      line.match(/(\r\n|\n|\r)/) ? <br key={index} /> : line
    );
  
    const urlRegex = /(https?:\/\/[^\s]+)/g;
  
    return lines.map((line, index) =>
      typeof line === 'string' ? (
        line.split(urlRegex).map((part, i) => 
          urlRegex.test(part) ? (
            <Link
              key={`${index}-${i}`}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
              underline="always"
            >
              {part}
            </Link>
          ) : highlight ? (
            part.split(new RegExp(`(${highlight})`, 'gi')).map((subPart, subIndex) =>
              subPart.toLowerCase() === highlight.toLowerCase() ? (
                <span
                  key={`${index}-${i}-${subIndex}`}
                  className="highlighted-text"
                >
                  {subPart}
                </span>
              ) : (
                subPart
              )
            )
          ) : (
            part
          )
        )
      ) : (
        line
      )
    );
  };

  return (
    <div>
      <h1>{selectedText || 'Genshin Impact'}</h1>
      <Box mb={2}>
        <Typography variant="h5">{isEditMode ? "Edit" : "Add"} Data</Typography>
        <form onSubmit={handleSubmit}>
          <Box display="grid" gap={1} gridTemplateColumns="repeat(2, 1fr)">
            <TextField
              label="UID"
              name="uid"
              value={formData.uid}
              onChange={handleChange}
              required
            />
            <TextField
              label="Server"
              name="server"
              value={formData.server}
              onChange={handleChange}
              required
            />
            <TextField
              label="Detail"
              name="detail"
              value={formData.detail}
              onChange={handleChange}
              required
              multiline
              rows={4}
            />
          </Box>
          <Box display="flex" gap={2} mt={2}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : isEditMode ? "Edit" : "Submit"}
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              onClick={handleReset}
            >
              Reset
            </Button>
          </Box>
        </form>

        {message && 
          <Snackbar 
            open={!!message} 
            autoHideDuration={3000} 
            message={message} 
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            onClose={() => setMessage("")}
          />
        }
      </Box>

      <TextField
        label="Masukan UID (min 4 karakter)"
        variant="filled"
        fullWidth
        value={searchId}
        onChange={(e) => setSearchId(e.target.value)}
        sx={{ '& .MuiTextField-root': { m: 1 }, marginBottom: '16px' }}
      />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '16px' }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {data.length > 0 ? (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id} align="left" style={{ width: column.width }}>
                      {column.label}
                    </TableCell>
                  ))}
                  <TableCell align="left">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                  <TableRow hover role="checkbox" tabIndex={-1} key={`${row.no}-${row.uid}`}>
                    {columns.map((column) => {
                      const value = row[column.id];
                      return (
                        <TableCell key={column.id} align="left">
                          {column.id === 'detail'
                            ? highlightText(value?.toString() || '', searchId)
                            : highlightText(value?.toString() || '', searchId)}
                        </TableCell>
                      );
                    })}
                    <TableCell>
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={() => handleEdit(row)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleDelete(row.uid)}
                      >
                        Hapus
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5]}
            component="div"
            count={data.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      ) : (
        <></>
      )}
    </div>
  );
};

export default GenshinImpact;
